import { View, Platform, StyleSheet, StyleProp, ViewStyle, DimensionValue, LayoutChangeEvent } from "react-native";
import React, { PropsWithChildren, useState, LegacyRef, useEffect } from "react";
import { useTheme } from "@/hooks/useTheme";
import useClickOutside from "@/hooks/useClickOutside";
import Animated, { AnimatedStyle, useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";

export type ContextMenuProps = PropsWithChildren & {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  style?: StyleProp<AnimatedStyle<StyleProp<ViewStyle>>>;
  position?: Partial<Record<"top" | "left" | "bottom" | "right", DimensionValue>>;
  ignoreRef?: React.RefObject<any>;
  clickOutside?: boolean;
  onDismiss?: (args?: any) => void;
  onLayout?: (event: LayoutChangeEvent) => void;
};

export default function ContextMenu({
  position,
  children,
  visible: controlledVisible,
  setVisible: setControlledVisible,
  ignoreRef,
  clickOutside = true,
  style,
  onDismiss,
  onLayout,
}: ContextMenuProps) {
  const { colors } = useTheme();
  const isControlled = controlledVisible !== undefined && setControlledVisible !== undefined;
  const [uncontrolledVisible, setUncontrolledVisible] = useState(false);
  const visible = isControlled ? controlledVisible : uncontrolledVisible;
  const setVisible = isControlled ? setControlledVisible : setUncontrolledVisible;
  const opacity = useSharedValue(0);

  const componentRef = useClickOutside(() => {
    if (clickOutside) {
      setVisible(false);
      onDismiss?.();
    }
  }, ignoreRef);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: opacity.get(),
    };
  });

  useEffect(() => {
    opacity.set(withTiming(visible ? 1 : 0, { duration: 150 }));
  }, [visible, opacity]);

  return (
    visible && (
      <Animated.View
        ref={Platform.select({
          web: componentRef as unknown as LegacyRef<View>,
          default: undefined,
        })}
        onLayout={onLayout}
        style={[
          { borderColor: colors.border, backgroundColor: visible ? colors.card : "" },
          styles.container,
          style,
          position ?? {},
          animatedStyle,
        ]}
      >
        {children}
      </Animated.View>
    )
  );
}

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    minWidth: 100,
    width: "auto",
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 9.99,
    zIndex: 2,
    elevation: 5,
    boxShadow: "0 2px 3.84px 0 rgba(0, 0, 0, 0.25)",
    padding: 8,
  },
  title: {
    padding: 8,
    flexWrap: "nowrap",
  },
});
